import { baseApi } from '@/Redux/baseApi';
import type { ApiScope, ApiClientStatus } from '@/Pages/Integrations/apiClientsApi';
import type { DeveloperAppCategory, DeveloperAppReviewStatus } from './devportalApi';

/**
 * # ADR-0027, the installing tenant's side, pinned here
 *
 * Read directly from `src/modules/devportal/`:
 *   - `marketplace-listings.controller.ts` / `.service.ts` — the catalogue
 *     every tenant browses (`LISTING_VIEW`, `APPROVED`-only)
 *   - `marketplace-installations.controller.ts` / `.service.ts` — what THIS
 *     tenant has installed (`INSTALLATION_VIEW`)
 *   - `dto/marketplace.dto.ts` for every request body
 *
 * Moderation (`devportal/review/*`, gated on `platform.manage_marketplace`,
 * held only by `PLATFORM_ADMIN`) is deliberately NOT pinned or built here —
 * approving a version publishes it to every tenant on the platform and
 * suspending an app reaches into every tenant that installed it, neither of
 * which is a single venue's decision. It belongs beside the platform's other
 * `platform.*` consoles (Platform-Dashboard), the same place the support
 * console (ADR-0019) already lives rather than this app.
 *
 * ## Approving a version never widens what an installed tenant already granted
 *
 * This is the one property this file exists to make visible, not just
 * correct. `MarketplaceListing.requestedScopes` is what the CURRENT approved
 * version asks of a NEW installer; `MarketplaceInstallation.grantedScopes` is
 * a snapshot of what THIS installer actually consented to, and the two can
 * disagree the moment the publisher ships a version the platform approves —
 * ADR-0027's own proof (`marketplace-installations.upgrade-consent.spec.ts`)
 * asserts they stay disagreeing until `consentToUpgrade` is called by name.
 * `MarketplacePanel`'s installation detail compares these two sets directly
 * (there is no "installed version number" visible to an installer — only a
 * publisher reads `DeveloperAppVersion` rows, on `developer_apps.view`, which
 * an installing tenant of someone else's app does not hold) and offers
 * re-consent only when they differ.
 */

export type MarketplaceInstallationStatus = 'ACTIVE' | 'SUSPENDED' | 'UNINSTALLED';

export const MARKETPLACE_ROUTES = {
  listings: '/marketplace/listings',
  listing: (id: string) => `/marketplace/listings/${id}`,
  installations: '/marketplace/installations',
  installation: (id: string) => `/marketplace/installations/${id}`,
  install: (developerAppId: string) => `/marketplace/installations/${developerAppId}`,
  consentUpgrade: (id: string) => `/marketplace/installations/${id}/consent-upgrade`,
  uninstall: (id: string) => `/marketplace/installations/${id}/uninstall`,
} as const;

/** The five keys `permissions.ts` registers under `marketplace`. `platform.manage_marketplace` is a separate, platform-only family — see the header note. */
export const MARKETPLACE_PERMISSIONS = {
  browse: 'marketplace.browse',
  install: 'marketplace.install',
  configure: 'marketplace.configure',
  uninstall: 'marketplace.uninstall',
  consentUpgrade: 'marketplace.consent_upgrade',
} as const;

/** `LISTING_VIEW` on the wire — always `status: 'APPROVED'` in practice, since `findAll`/`findOne` both filter to it. */
export interface MarketplaceListing {
  id: string;
  developerAppId: string;
  slug: string;
  name: string;
  description: string | null;
  category: DeveloperAppCategory;
  publisherName: string;
  homepageUrl: string | null;
  privacyUrl: string | null;
  supportUrl: string | null;
  logoUrl: string | null;
  currentVersion: number | null;
  requestedScopes: ApiScope[];
  status: DeveloperAppReviewStatus;
  publishedAt: string | null;
}

export interface MarketplaceInstallationApiClient {
  id: string;
  clientId: string;
  status: ApiClientStatus;
  scopes: ApiScope[];
  lastUsedAt: string | null;
}

/** `INSTALLATION_VIEW` on the wire. */
export interface MarketplaceInstallation {
  id: string;
  developerAppId: string;
  installedVersionId: string;
  /** What was actually consented to — a snapshot, not a live read of the version. See the header note. */
  grantedScopes: ApiScope[];
  status: MarketplaceInstallationStatus;
  installedById: string | null;
  uninstalledAt: string | null;
  suspendedAt: string | null;
  suspendedReason: string | null;
  createdAt: string;
  apiClient: MarketplaceInstallationApiClient;
}

/** `POST /marketplace/installations/:developerAppId` answers with a live secret exactly once. */
export interface MarketplaceInstallationWithSecret extends MarketplaceInstallation {
  apiClient: MarketplaceInstallationApiClient & { clientSecret: string };
}

/**
 * `acceptedScopes` must equal the listing's `requestedScopes` exactly — a
 * subset silently under-grants, a superset silently over-grants. The backend
 * refuses either with `code: 'SCOPES_MISMATCH'`; this screen presents the
 * scope list as a fixed confirmation rather than a picker so that mismatch is
 * not something a caller can even construct client-side.
 */
export interface InstallMarketplaceAppBody {
  acceptedScopes: ApiScope[];
  branchId?: string;
}

/** Same "must match exactly" shape as install, targeted at the listing's CURRENT approved version. */
export interface ConsentUpgradeBody {
  acceptedScopes: ApiScope[];
}

/** The ONLY reconfiguration available without a fresh consent screen — scopes cannot move through this route. */
export interface ConfigureInstallationBody {
  branchId?: string | null;
}

export interface UninstallMarketplaceAppBody {
  reason?: string;
}

export const marketplaceApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /marketplace/listings` — `marketplace.browse`. */
    marketplaceListings: build.query<MarketplaceListing[], { category?: DeveloperAppCategory } | void>({
      query: (params) => ({ url: MARKETPLACE_ROUTES.listings, params: params ?? undefined }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as MarketplaceListing[]) : [],
      providesTags: (result) => [
        { type: 'Marketplace' as const, id: 'LISTINGS' },
        ...(result ?? []).map((l) => ({ type: 'Marketplace' as const, id: `listing:${l.id}` })),
      ],
    }),

    /** `GET /marketplace/listings/:id` — `marketplace.browse`. */
    marketplaceListing: build.query<MarketplaceListing | null, string>({
      query: (id) => ({ url: MARKETPLACE_ROUTES.listing(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as MarketplaceListing) : null,
      providesTags: (_r, _e, id) => [{ type: 'Marketplace' as const, id: `listing:${id}` }],
    }),

    /** `GET /marketplace/installations` — `marketplace.install` (the read, not just the write — matches the controller's own gate). */
    marketplaceInstallations: build.query<MarketplaceInstallation[], void>({
      query: () => ({ url: MARKETPLACE_ROUTES.installations }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as MarketplaceInstallation[]) : [],
      providesTags: (result) => [
        { type: 'Marketplace' as const, id: 'INSTALLATIONS' },
        ...(result ?? []).map((i) => ({ type: 'Marketplace' as const, id: `installation:${i.id}` })),
      ],
    }),

    /** `GET /marketplace/installations/:id` — `marketplace.install`. */
    marketplaceInstallation: build.query<MarketplaceInstallation | null, string>({
      query: (id) => ({ url: MARKETPLACE_ROUTES.installation(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as MarketplaceInstallation) : null,
      providesTags: (_r, _e, id) => [{ type: 'Marketplace' as const, id: `installation:${id}` }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useMarketplaceListingsQuery,
  useMarketplaceListingQuery,
  useMarketplaceInstallationsQuery,
  useMarketplaceInstallationQuery,
} = marketplaceApi;

/** An installation changed — installed, configured, re-consented, or uninstalled. */
export function invalidateMarketplaceInstallation(id?: string) {
  return marketplaceApi.util.invalidateTags([
    { type: 'Marketplace' as const, id: 'INSTALLATIONS' },
    ...(id ? [{ type: 'Marketplace' as const, id: `installation:${id}` }] : []),
  ]);
}
